/* eslint-disable complexity -- legacy code */
import { useToken } from '@ori/auth';
import { useSWR } from '@ori/fetching';
import type {
  ApiClient as GlobalApiClient,
  GetCustomerMenuOrderType,
  GlobalApplicationsPersonalMenuPersonalMenuDto,
} from '@ori/global-api-client-react';
import {
  getApplicationsPersonalMenuOrisalesMenu,
  getCustomerMenu,
  useApiClient as useGlobalApiClient,
} from '@ori/global-api-client-react';
import { useLanguage } from '@ori/i18n';
import { z } from 'zod';

import { useConfiguration } from '../contexts/configurationContext';
import { mainLogger } from '../logger';
import type { PersonalMenuInitialData } from '../models';
import { type MenuItem, menuItemSchema } from '../schemas/menuItemSchema';
import { userSchema, type User } from '../schemas/userSchema';
import { createSessionStorageKey } from '../utils/createSessionStorageKey';
import { findSelectedPath } from '../utils/findSelectedPath';
import { isOrisalesAvailable } from '../utils/isOrisalesAvailable';
import { mapCustomerMenuToInternalMenuItems } from '../utils/mapCustomerMenuToInternalMenuItems';
import { mapOrisalesToInternalMenuItems } from '../utils/mapOrisalesToInternalMenuItems';
import { mergeOrisalesWithPersonalMenuItems } from '../utils/mergeOrisalesWithPersonalMenuItems';
import { withSessionStorageCache } from '../utils/withSessionStorageCache';

const logger = mainLogger.createChild('usePersonalMenu');

export const getUserFromPersonalMenu = ({
  userHash,
  customerLastName,
  customerFirstName,
  userType,
}: Omit<GlobalApplicationsPersonalMenuPersonalMenuDto, 'items'>): User | null =>
  userHash
    ? {
        firstName: customerFirstName ?? null,
        lastName: customerLastName ?? null,
        userHash,
        userType,
      }
    : null;

type GetUserOptions = {
  globalApiClient: GlobalApiClient;
  customerId: number;
  orderType: GetCustomerMenuOrderType | undefined;
  initialData?: PersonalMenuInitialData;
};
const getUserCached = withSessionStorageCache(
  {
    key: ({ customerId, orderType, initialData }) =>
      createSessionStorageKey(['user', customerId, orderType, initialData?.userHash]),
    schema: userSchema,
  },
  async ({ customerId, globalApiClient, orderType, initialData }: GetUserOptions): Promise<User> => {
    const userFromInitialData = initialData && getUserFromPersonalMenu(initialData);

    if (userFromInitialData) {
      return userFromInitialData;
    }

    const { data } = await getCustomerMenu(globalApiClient, String(customerId), {
      orderType,
      rootLevelOnly: true,
    });

    const userFromMenu = getUserFromPersonalMenu(data);

    if (!userFromMenu) {
      throw new Error('Could not get user from personal menu due to missing userHash');
    }

    return userFromMenu;
  },
);

type GetCustomerMenuItemOptions = {
  globalApiClient: GlobalApiClient;
  customerId: number;
  orderType: GetCustomerMenuOrderType | undefined;
  userHash: string;
};
const getCustomerMenuItemsCached = withSessionStorageCache(
  {
    key: ({ customerId, orderType, userHash }) =>
      createSessionStorageKey(['menu-items', customerId, orderType, userHash]),
    schema: z.array(menuItemSchema),
  },
  async (options: GetCustomerMenuItemOptions) => {
    const { customerId, globalApiClient, orderType, userHash } = options;
    const { data: customerMenu } = await getCustomerMenu(globalApiClient, String(customerId), {
      rootLevelOnly: false,
      orderType,
      userHash,
    });

    return mapCustomerMenuToInternalMenuItems('', customerMenu.items);
  },
);

type GetCustomerOrisalesMenuOptions = {
  globalApiClient: GlobalApiClient;
  consultantNumber: string | undefined;
  userType: User['userType'];
  language: string;
};
const getCustomerOrisalesMenuCached = withSessionStorageCache(
  {
    key: ({ consultantNumber, userType }) =>
      createSessionStorageKey(['orisales-menu-items', consultantNumber, userType]),
    schema: z.array(menuItemSchema).nullable(),
  },
  async (options: GetCustomerOrisalesMenuOptions) => {
    const { consultantNumber, globalApiClient, userType, language } = options;
    const canFetchOrisalesMenu = consultantNumber !== undefined && isOrisalesAvailable(userType);

    if (!canFetchOrisalesMenu) {
      return null;
    }

    const { data } = await getApplicationsPersonalMenuOrisalesMenu(
      globalApiClient,
      {},
      {
        params: {
          distNum: consultantNumber,
          // FIXME: Why is orisales route not taking the accept language header?
          locale: language,
        },
      },
    );

    return mapOrisalesToInternalMenuItems('', data.items);
  },
);

export const usePersonalMenu = () => {
  const { customerId, formattedToken } = useToken();
  const { language } = useLanguage();
  const configuration = useConfiguration();
  const globalApiClient = useGlobalApiClient();

  return useSWR(
    // Leave in formattedToken as part of the key so the globalApiClient is passed to fetcher correctly
    [`menu-items`, language, customerId, configuration, formattedToken],
    async ([, currentLanguage, currentCustomerId, currentConfiguration]) => {
      const {
        personalMenuInitialData: initialData,
        consultantNumber,
        consultantType,
        reportsItemName,
      } = currentConfiguration;
      const { orderType, useCaching = true } = initialData ?? {};

      const user = await getUserCached(
        {
          globalApiClient,
          customerId: currentCustomerId,
          orderType,
          initialData: initialData ?? undefined,
        },
        {
          pauseCaching: !useCaching,
        },
      );

      const customerMenuItems = await getCustomerMenuItemsCached(
        {
          globalApiClient,
          customerId,
          orderType,
          userHash: user.userHash,
        },
        { pauseCaching: !useCaching },
      );

      let orisalesMenuItems: MenuItem[] = [];

      // Orisales is being deprecated and some environments and some markets does not have enough data and API call are failing
      // Adding try/catch here prevents whole presonal menu from failing. The data are not required and the download will be tried on every request
      try {
        orisalesMenuItems =
          (await getCustomerOrisalesMenuCached(
            {
              globalApiClient,
              consultantNumber,
              userType: user.userType,
              language: currentLanguage,
            },
            {
              pauseCaching: !useCaching,
            },
          )) ?? [];
      } catch (error) {
        logger.error(new Error('Failed to fetch the orisales menu items', { cause: error }));
      }

      // Aka personal menu
      const menuItemsWithOrisales = mergeOrisalesWithPersonalMenuItems(
        customerMenuItems,
        orisalesMenuItems,
        consultantType,
        reportsItemName,
      );

      return {
        user,
        customerMenuItems,
        orisalesMenuItems,
        items: menuItemsWithOrisales,
        hasSubitems: !!menuItemsWithOrisales.length || !!customerMenuItems.length || !!orisalesMenuItems?.length,
      };
    },
    { refreshInterval: 0, revalidateOnFocus: false, revalidateIfStale: false, revalidateOnReconnect: false },
  );
};

export const useSelectedMenuItem = () => {
  const { data } = usePersonalMenu();

  return data?.items ? findSelectedPath(data.items) : undefined;
};
